import {
  ChangeDetectorRef,
  Component,
  OnInit,
  Type,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EaDisplay } from '@app/components/pr-display';
import {
  PrTable,
  PrTableColumn,
} from '@app/components/pr-table/pr-table.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { SimulationDao } from '@app/dao/simulation/simulation.dao';
import { SimulationType } from '@app/dao/simulation/simulation.interface';
import { UserDao } from '@app/dao/user/user.dao';
import {
  SimulationInformations,
  SimulationParameters,
} from '@app/dashboard/shared/ea-simulation-parameters/ea-simulation-parameters.component';
import { EaSimulationPacksComponent } from '@app/dashboard/sheets/shared/ea-simulation-packs/ea-simulation-packs.component';
import {
  SimulationPack,
  SimulationPackService,
} from '@app/dashboard/sheets/shared/ea-simulation-packs/simulation-pack.service';
import { SheetsService } from '@app/dashboard/sheets/sheets.service';
import { EaSimulationAvFormComponent } from '@app/forms/simulation/ea-simulation-av-form/ea-simulation-av-form.component';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { ProductsService, ProductsType } from '@app/providers/products.service';
import * as _ from 'lodash';
import { throwError, catchError, finalize, map, shareReplay, take, takeWhile } from 'rxjs';
import { cashOrCreditOfScpiOwnership } from './scpi-ownership-financing';
import { leavePackStepWithoutPack } from '../../pledge-pack-choice';

export interface AvPayload {
  allocationModelId?: any;
  productId?: any;
  dependentChildCount?: number;
  durationInYears?: number;
  expenseList?: any;
  incomeList?: any;
  initialDeposit?: number;
  mainResidence?: any;
  maritalStatus?: string;
  monthlyDeposit?: number;
  personalUseList?: any;
  phoneNumber?: any;
  rentalAssetList?: any;
  riskProfile?: any;
  riskProfileScore?: number;
  supportList?: any;
  taxAmount?: number;
  taxDeductionReductionList?: any;
  taxShareCount?: number;
  tmi?: number;
  totalActualCosts?: number;
  totalIncome?: number;
}
export interface SCPIPayload {
  investAmount?: number;
  projectDuration?: number;
  dismembermentDuration?: number;
  partRevalorisationRate?: number;
  rentalReversionRate?: number;
  loanDuration?: number;
  teg?: number;
  funding?: number | string;
  scpiList?: any[];
  scpiPack?: any;
  cashOrCredit?: 'CASH' | 'CREDIT';
}
export interface SCPIGraph {
  creditAmountDue: number;
  creditBehoofSum: number;
  creditBehoofSumTotal: number;
  creditInsuranceAmount: number;
  creditRent: number;
  gain: number;
  gainLivretA: number;
  investAmount: number;
  investAmountLivretA: number;
  key: string;
  partShare: any[];
  patrimony: number;
  rent: number;
  taxCapitalGain: number;
  taxRent: number;
}

const DEFAULT_PARAMETERS: SCPIPayload = {};
@Component({
    selector: 'simulation-packs-scpi',
    templateUrl: './simulation-packs-scpi.component.html',
    styleUrls: ['./simulation-packs-scpi.component.scss'],
    standalone: false
})
export class SimulationPacksScpiComponent implements OnInit {
  selected: boolean = false;
  productsType = ProductsType;
  form: Type<any> = EaSimulationAvFormComponent;
  formOptions;

  defaultParameters: Array<SimulationParameters | SimulationInformations> = [
    {
      label: "Paramètres de l'investissement",
      parameters: [
        {
          icon: 'sp-simu-cash',
          label: 'Investissement',
          tooltip: this.sheetsService.simulationsTooltip.investedAmount,
          value: simulation => simulation.parameters.investedAmount,
          suffix: '€',
        },
        {
          icon: 'sp-simu-gavel',
          label: 'Type d’investissement ',
          tooltip: 'Type d’investissement ',
          value: simulation =>
            simulation.parameters.ownership == 'FULL_OWNERSHIP'
              ? 'Pleine propriété'
              : 'Nue propriété',
          suffix: '',
        },
        {
          icon: 'sp-simu-clock',
          label: 'Durée du projet',
          tooltip: 'Durée du projet',
          value: simulation => simulation.parameters.projectDuration / 12,
          suffix: ' ans',
        },
        {
          icon: 'sp-simu-clock',
          label: 'Durée du démembrement',
          tooltip: 'Durée du démembrement',
          value: simulation => simulation.parameters.dismembermentDuration,
          suffix: ' ans',
          showOnlyIf: simulation =>
            simulation.parameters.ownership != 'FULL_OWNERSHIP',
        },
        {
          icon: 'sp-simu-stat-up',
          label: 'Revalorisation foncière',
          tooltip: this.sheetsService.simulationsTooltip.partRevalorisationRate,
          value: simulation =>
            simulation.parameters.partRevalorisationRate * 100,
          suffix: '%',
        },
        {
          icon: 'sp-simu-stat',
          label: 'Revalorisation locative',
          tooltip: this.sheetsService.simulationsTooltip.rentalReversionRate,
          value: simulation => simulation.parameters.rentalReversionRate * 100,
          suffix: '%',
        },
      ],
    },
    {
      label: simulation =>
        simulation && simulation.parameters.ownership == 'BARE_OWNERSHIP'
          ? 'L’investissement en nue propriété ne peut pas être effectué à crédit '
          : 'Paramètres du financement',
      parameters: [
        {
          icon: 'sp-simu-cash',
          label: 'Financement',
          tooltip: 'Financement',
          value: simulation =>
            simulation.parameters.cashOrCredit == 'CREDIT'
              ? 'à credit'
              : 'en cash',
          suffix: null,
        },
        {
          icon: 'sp-simu-clock',
          label: 'Durée du credit',
          tooltip: this.sheetsService.simulationsTooltip.loanDuration,
          value: simulation => simulation.parameters.loanDuration,
          suffix: 'mois',
          showOnlyIf: simulation =>
            simulation.parameters &&
            simulation.parameters.cashOrCredit == 'CREDIT',
        },
        {
          icon: 'sp-simu-building',
          label: 'Taux du credit',
          tooltip: this.sheetsService.simulationsTooltip.loanRate,
          value: simulation => simulation.parameters.loanRate,
          suffix: '%',
          showOnlyIf: simulation =>
            simulation.parameters &&
            simulation.parameters.cashOrCredit == 'CREDIT',
        },
        {
          icon: 'sp-simu-building',
          label: "Taux d'assurance",
          tooltip: this.sheetsService.simulationsTooltip.loanInsuranceRate,
          value: simulation => simulation.parameters.loanInsuranceRate,
          suffix: '%',
          showOnlyIf: simulation =>
            simulation.parameters &&
            simulation.parameters.cashOrCredit == 'CREDIT',
        },
        {
          icon: 'sp-simu-pig',
          label: 'Frais bancaires',
          tooltip: 'Frais bancaires',
          value: simulation => simulation.parameters.bankingFees,
          suffix: '€',
          showOnlyIf: simulation =>
            simulation.parameters &&
            simulation.parameters.cashOrCredit == 'CREDIT',
        },
      ],
      showContent: simulation =>
        simulation && simulation.parameters.ownership == 'BARE_OWNERSHIP'
          ? true
          : false,
      content:
        'Il n’est possible d’acquérir des parts de SCPI en nue-propriété qu’en direct.  Il n’est pas possible non plus de souscrire un prêt immobilier car les parts en nue-propriété ne génèrent pas de cash flow. Il faut donc que le paiement soit réalisé comptant.',
      display: EaDisplay.warning,
    },
  ];
  parameters = this.defaultParameters;
  loading: boolean = true;
  simulation: any;
  @ViewChild(EaSimulationPacksComponent, { read: SimulationPackService })
  simulationPackService: SimulationPackService;
  packsTableColumnsDefinition: PrTableColumn[];
  lineOptions;
  _ = _;
  score = undefined;

  private alive = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductsService,
    private userDao: UserDao,
    private referenceDao: ReferenceDao,
    private simulationDao: SimulationDao,
    public cd: ChangeDetectorRef,
    private sheetsService: SheetsService,
    private subscriptionFacade: SubscriptionFacadeService,
  ) {}

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    const owners = pledge.owners;
    this.userDao
      .getAuthentified()
      .pipe(take(1))
      .subscribe(user => {
        const { score } =
          owners?.find(owner => owner.id === user.id) || owners?.[0] || {};

        this.score = score;
      });
  }
  ngAfterViewInit() {
    // this.cd.detectChanges();

    const parameters: SCPIPayload = {
      ...DEFAULT_PARAMETERS,
      cashOrCredit: cashOrCreditOfScpiOwnership(
        this.subscriptionFacade.getPledge()?.parameters?.scpiOwnershipType,
      ),
    };
    this.simulation = { parameters };

    this.simulate(parameters)
      .pipe(
        takeWhile(() => this.alive),
        shareReplay(1),
      )
      .subscribe();
    this.simulate({ scpiPack: null });
    this.simulationPackService.allocationFormSubject
      .pipe(
        takeWhile(() => this.alive),
        shareReplay(1),
      )
      .subscribe(() => {
        this.simulate({ scpiPack: null }).subscribe(() => {
          //this.cd.detectChanges();
        });
      });
  }

  simulate(payload?: SCPIPayload, id?: any) {
    this.loading = true;
    this.simulation.parameters = { ...this.simulation.parameters, ...payload };

    if (
      !this.simulation.parameters.supportList &&
      this.simulationPackService.selectedPack
    ) {
      if (this.simulationPackService.selectedPack.id) {
        this.simulation.parameters.supportList =
          this.simulationPackService.selectedPack.allocation;
      } else {
        this.simulation.parameters.allocationModelId =
          this.simulationPackService.selectedPack.allocation;
      }
    }

    return this.simulationDao
      .simulate(
        id ? id : this.simulation.id,
        SimulationType.SCPI,
        this.simulation.parameters,
      )
      .pipe(
        takeWhile(() => this.alive),
        catchError(() => {
          if (id) {
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { id: null },
              queryParamsHandling: 'merge',
            });

            return throwError(
              'Error when loading simulation, reloading page without id param',
            );
          }
        }),
        map(simulation => {
          this.setSimulation(simulation);
        }),
        shareReplay(1),
        finalize(() => {
          this.loading = false;
        }),
      );
  }

  setSimulation(simulation) {
    this.simulation = simulation;

    if (this.simulation.parameters.productId)
      this.simulationPackService.productId =
        this.simulation.parameters.productId;
    // this.loading = false;
    this.simulationPackService
      .getPacks()
      .pipe(takeWhile(() => this.alive))
      .subscribe(packs => {
        leavePackStepWithoutPack(packs, this.subscriptionFacade);
        this.setPack(packs);
        if (this.simulation.parameters.allocationModelId) {
          this.setPack(packs);
        }
        this.loading = false;
        //this.cd.detectChanges();
      });
  }
  setPack(packs: any) {
    const packId = this.subscriptionFacade.getPledge()?.parameters?.scpiPackId;

    const pack = packs?.find(pack => pack.id === packId);

    !this.simulationPackService.selectedPack &&
      this.simulationPackService.selectPack(pack);
  }

  onPackSelected(pack: SimulationPack) {
    this.simulationPackService.selectPack(pack);
    this.selected = true;
  }

  turnPackAllocationToDTO() {
    return (this.simulation.parameters.supportList = _.map(
      this.simulationPackService?.selectedPack?.allocation,
      (allocation: any) => {
        return {
          isinCode: allocation.supportIsin || allocation.isin,
          allocationPercentage: allocation.allocationPercentage,
        };
      },
    ));
  }

  onParametersEdit(event) {
    if (event.productId != this.simulation.parameters.productId)
      this.simulation.parameters.allocationModelId = undefined;
    this.simulate(event)
      .pipe(takeWhile(() => this.alive))
      .subscribe();
  }

  linePatrimony;

  initLineOptions() {
    let currentYear = new Date().getFullYear();
    let xCategories = _.range(currentYear, currentYear + 16);

    let series = [];

    series.push({
      name: 'Fourchette haute',
      color: '#33A02C',
      data: this.result.projection.minAmountsByProbability['0.1'],
    });

    series.push({
      name: 'Médiane',
      color: '#FDBF6F',
      data: this.result.projection.minAmountsByProbability['0.5'],
    });
    series.push({
      name: 'Fourchette basse',
      color: '#A6CEE3',
      data: this.result.projection.minAmountsByProbability['0.9'],
    });

    if (this.simulation.parameters.initialDeposit) {
      series.push({
        name: 'Somme investie',
        color: '#1F78B4',
        data: _.map(
          _.range(16),
          i => this.result.projection.spentAmountByYears[i],
        ),
      });
    }
    this.lineOptions = this.getLineOptions(series, xCategories);
  }

  getLineOptions(series: any[], xCategories: any[]): any {
    let options = {
      chart: {
        type: 'line',
        spacingLeft: 0,
        spacingRight: 0,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: xCategories,
      },
      yAxis: {
        title: {
          text: '',
        },
        allowDecimals: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        valueSuffix: '€',
        valueDecimals: 0,
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: false,
          },
          marker: {
            symbol: 'circle',
          },
        },
      },
      series: series,
    };

    return options;
  }

  projectionTable: PrTable;
  projectionTableDefinitionColumn = [
    new PrTableColumn('FOURCHETTE', 'name', ''),
    new PrTableColumn('5 ANS', '5Y', 'currency'),
    new PrTableColumn('8 ANS', '8Y', 'currency'),
    new PrTableColumn('10 ANS', '10Y', 'currency'),
    new PrTableColumn('12 ANS', '12Y', 'currency'),
    new PrTableColumn('15 ANS', '15Y', 'currency'),
  ];

  initProjectionTable() {
    let table = [
      {
        name: 'Fourchette haute',
        ...this.setProjectionTableProperties('0.1'),
      },
      {
        name: 'Médiane',
        ...this.setProjectionTableProperties('0.5'),
      },
      {
        name: 'Fourchette basse',
        ...this.setProjectionTableProperties('0.9'),
      },
      {
        name: 'Somme investie',
        '5Y': this.result.projection.spentAmountByYears[5],
        '8Y': this.result.projection.spentAmountByYears[8],
        '10Y': this.result.projection.spentAmountByYears[10],
        '12Y': this.result.projection.spentAmountByYears[12],
        '15Y': this.result.projection.spentAmountByYears[15],
      },
    ];

    this.projectionTable = new PrTable(
      table,
      this.projectionTableDefinitionColumn,
    );
  }

  //Shorthand table attribution
  setProjectionTableProperties(property) {
    return {
      '5Y': this.result.projection.minAmountsByProbability[property][5],
      '8Y': this.result.projection.minAmountsByProbability[property][8],
      '10Y': this.result.projection.minAmountsByProbability[property][10],
      '12Y': this.result.projection.minAmountsByProbability[property][12],
      '15Y': this.result.projection.minAmountsByProbability[property][15],
    };
  }

  ngOnDestroy() {
    this.alive = false;
  }

  get productType() {
    return this.productService
      .getProducts()
      .find(p => p.title == ProductsType.ASSURANCE_VIE);
  }

  get result() {
    return this.simulation && this.simulation.result;
  }
  extract() {
    return {
      scpiPackId: this.simulationPackService.selectedPack.id,
    };
  }
  onSubmit() {
    const scpiPackId = this.subscriptionFacade.getPledge()?.parameters
      ?.scpiPackId as any;
    if (this.simulationPackService.selectedPack) {
      if (!this.selected) {
        this.subscriptionFacade.nextStep();
        return;
      }
      if (
        scpiPackId &&
        scpiPackId != this.simulationPackService.selectedPack.id
      ) {
        this.subscriptionFacade.submitPledgeWithoutForm(
          this,
          'pledgeAndResetProduct',
        );
      } else {
        this.subscriptionFacade.submitPledgeWithoutForm(this);
      }
    }
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
}
